import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import http from '../api/http';
import { Bell, CheckCircle, Trash2, ArrowLeft } from 'lucide-react';

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous'); // tous, non_lues

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/notifications');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      await http.put(`/api/notifications/${id}/lu`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, lu: 1 } : n));
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await http.put('/api/notifications/tout-lu');
      setNotifications(prev => prev.map(n => ({ ...n, lu: 1 })));
    } catch (error) {
      console.error('Erreur marquage tout lu:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await http.delete(`/api/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Erreur suppression notification:', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    await markAsRead(notif.id);
    if (notif.lien) {
      navigate(notif.lien);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays < 7) return `Il y a ${diffDays} j`;
    return date.toLocaleDateString('fr-FR');
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'non_lues') return !notif.lu;
    return true;
  });

  const nonLuesCount = notifications.filter(n => !n.lu).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Notifications</h1>
              <p className="text-gray-600 mt-1">
                {nonLuesCount} notification{nonLuesCount > 1 ? 's' : ''} non lue{nonLuesCount > 1 ? 's' : ''}
              </p>
            </div>
            {nonLuesCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <CheckCircle className="h-4 w-4" />
                Tout marquer comme lu
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setFilter('tous')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'tous' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter('non_lues')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'non_lues' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Non lues
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Chargement...</div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="rounded-2xl bg-white p-12 text-center shadow-sm">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-600">
              {filter === 'non_lues' ? 'Aucune notification non lue' : 'Aucune notification'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`cursor-pointer rounded-2xl bg-white p-6 shadow-sm transition-colors hover:bg-gray-50 ${
                  !notif.lu ? 'border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-800">{notif.titre}</h3>
                      {!notif.lu && (
                        <span className="flex h-2 w-2 rounded-full bg-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{notif.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{formatTimeAgo(notif.created_at)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notif.id);
                    }}
                    className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
